import type * as React from 'react';
import type { ContentConfig } from '@ai-content-hub/content-core';
import { registerCompositeQuestionType } from './registry';
import { MCQEdit } from '../mcq/MCQEdit';
import { mcqElementSchema } from '../mcq/schema';
import { MatchEdit } from '../match/MatchEdit';
import { matchElementSchema, normalizeMatchData, type MatchElementData } from '../match/schema';
import { FITBEdit } from '../fill-in-the-blanks/FITBEdit';
import { fitbElementSchema, defaultFITBConfig } from '../fill-in-the-blanks/schema';
import { OrderingEdit } from '../ordering/OrderingEdit';
import { orderingElementSchema } from '../ordering/schema';
import { RecordAudioEdit } from '../record-audio/RecordAudioEdit';
import { recordAudioElementSchema } from '../record-audio/schema';

export const BUILTIN_COMPOSITE_QUESTION_TYPE_IDS = [
    'mcq',
    'match',
    'ordering',
    'fitb-dropdown',
    'fitb-type',
    'record-audio',
] as const;

export type BuiltinCompositeQuestionTypeId = (typeof BUILTIN_COMPOSITE_QUESTION_TYPE_IDS)[number];

function registerBuiltins(): void {
    registerCompositeQuestionType({
        id: 'mcq',
        label: 'Multiple Choice',
        createDefaultData: () => ({ ...mcqElementSchema.defaultData }),
        EditComponent: MCQEdit as React.ComponentType<Record<string, unknown>>,
        editorClassName: 'border-0 rounded-none space-y-0 p-0',
    });

    registerCompositeQuestionType({
        id: 'match',
        label: 'Matching',
        createDefaultData: () =>
            normalizeMatchData(matchElementSchema.defaultData as MatchElementData),
        EditComponent: MatchEdit as React.ComponentType<Record<string, unknown>>,
    });

    registerCompositeQuestionType({
        id: 'ordering',
        label: 'Ordering',
        createDefaultData: () => ({ ...orderingElementSchema.defaultData }),
        EditComponent: OrderingEdit as React.ComponentType<Record<string, unknown>>,
    });

    registerCompositeQuestionType({
        id: 'fitb-dropdown',
        label: 'Dropdown',
        createDefaultData: () => ({ ...fitbElementSchema.defaultData }),
        defaultConfig: {
            ...defaultFITBConfig,
            blankMode: 'dropdown',
        } as ContentConfig,
        EditComponent: FITBEdit as React.ComponentType<Record<string, unknown>>,
    });

    registerCompositeQuestionType({
        id: 'fitb-type',
        label: 'Fill in the Blanks',
        createDefaultData: () => ({ ...fitbElementSchema.defaultData }),
        defaultConfig: {
            ...defaultFITBConfig,
            blankMode: 'type',
        } as ContentConfig,
        EditComponent: FITBEdit as React.ComponentType<Record<string, unknown>>,
    });

    registerCompositeQuestionType({
        id: 'record-audio',
        label: 'Record audio',
        createDefaultData: () => ({ ...recordAudioElementSchema.defaultData }),
        EditComponent: RecordAudioEdit as React.ComponentType<Record<string, unknown>>,
        editorClassName: 'border-0 rounded-none space-y-0 p-0',
    });
}

registerBuiltins();
